import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { initBank, mergeHistory, clearHistory, clearActiveExam, clearDrill } from "@/lib/bank";
import { syncProgress, clearProgress } from "@/lib/progress";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);
const GUEST_KEY = "lexv_guest";
const readGuest = () => { try { return JSON.parse(localStorage.getItem(GUEST_KEY)); } catch { return null; } };

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [guest, setGuest] = useState(readGuest);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [bankRev, setBankRev] = useState(0);

  const syncHistory = useCallback(async () => {
    try { const { data } = await api.get("/history"); mergeHistory(data); } catch { /* offline ok */ }
    syncProgress();
  }, []);

  const clearLocal = useCallback(async () => {
    localStorage.removeItem("lexv_token"); clearHistory(); clearActiveExam(); clearDrill(); clearProgress(); setUser(null);
    await initBank();
    setBankRev((r) => r + 1);
  }, []);

  // Catch a sign-in on another device even when the page makes no API calls
  useEffect(() => {
    const check = () => { if (localStorage.getItem("lexv_token") && document.visibilityState === "visible") api.get("/auth/me").catch(() => {}); };
    window.addEventListener("focus", check);
    document.addEventListener("visibilitychange", check);
    const t = setInterval(check, 60000);
    return () => { window.removeEventListener("focus", check); document.removeEventListener("visibilitychange", check); clearInterval(t); };
  }, []);

  useEffect(() => {
    // Returning from Google: AuthCallback exchanges the session first
    if (window.location.hash?.includes("session_id=")) { setReady(true); return; }
    if (!localStorage.getItem("lexv_token")) { setReady(true); return; }
    api.get("/auth/me").then(({ data }) => { setUser(data); syncHistory(); })
      .catch(() => localStorage.removeItem("lexv_token")).finally(() => setReady(true));
  }, [syncHistory]);

  useEffect(() => {
    const h = () => { if (localStorage.getItem("lexv_token")) { clearLocal(); setSessionEnded(true); } };
    window.addEventListener("lexv-session-ended", h);
    return () => window.removeEventListener("lexv-session-ended", h);
  }, [clearLocal]);

  const applySession = async (data) => {
    localStorage.setItem("lexv_token", data.token);
    await Promise.all([syncHistory(), initBank()]);
    setBankRev((r) => r + 1);
    setUser(data.user);
    return data.user;
  };
  const authenticate = async (path, body) => {
    const { data } = await api.post(`/auth/${path}`, body);
    const u = await applySession(data);
    return data.verification_sent === false ? { ...u, verification_sent: false } : u;
  };
  const refresh = async () => { const { data } = await api.get("/auth/me"); await initBank(); setBankRev((r) => r + 1); setUser(data); return data; };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* already signed out */ }
    await clearLocal();
  };

  const captureLead = async (body) => {
    const { data } = await api.post("/leads/capture-trial", body);
    const g = { token: data.guest_token, first_name: data.first_name };
    localStorage.setItem(GUEST_KEY, JSON.stringify(g));
    setGuest(g);
    return g;
  };

  const value = {
    user, ready, guest, sessionEnded, setSessionEnded, setUser, applySession, refresh, logout, captureLead, bankRev,
    canDemo: !!user || !!guest, hasAccess: !!user?.has_access,
    login: (b) => authenticate("login", b), register: (b) => authenticate("register", b),
    google: (session_id) => authenticate("google", { session_id }),
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};
